import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import '../stili/GiocatoreForm.css';
import { useAuthFetch } from '../hooks/useAuthFetch';

const API_URL = process.env.REACT_APP_API_URL;

const RUOLI = ['PORTIERE', 'DIFENSORE', 'CENTROCAMPISTA', 'ATTACCANTE'];
const PIEDI = ['DESTRO', 'SINISTRO', 'AMBIDESTRO'];

export default function GiocatoreForm() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [categorie, setCategorie] = useState([]);
  const [stagioni, setStagioni] = useState([]);
  const [genitori, setGenitori] = useState([]);

  const [formData, setFormData] = useState({
    idCategoria: '',
    idStagione: '',
    idGenitore: '',
    codiceFiscale: '',
    cognome: '',
    nome: '',
    dataNascita: '',
    luogoNascita: '',
    numeroTesserino: '',
    piedePreferito: '',
    ruolo: '',
    noteSanitarie: ''
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  useEffect(() => {
    const fetchBase = async () => {
      try {
        const resCat = await authFetch(`${API_URL}/api/admin/categorie`);
        if (!resCat.ok) throw new Error('Errore fetch categorie');
        const dataCat = await resCat.json();
        setCategorie(Array.isArray(dataCat) ? dataCat : []);

        const resStag = await authFetch(`${API_URL}/api/admin/stagioni`);
        if (!resStag.ok) throw new Error('Errore fetch stagioni');
        const dataStag = await resStag.json();
        const stagList = Array.isArray(dataStag) ? dataStag : [];
        setStagioni(stagList);

        setFormData((prev) => {
          if (prev.idStagione) return prev;
          const attivaInLista = stagList.find((s) => s.attiva === true);
          if (attivaInLista?.id) {
            return { ...prev, idStagione: String(attivaInLista.id) };
          }
          return prev;
        });

        const resGen = await authFetch(`${API_URL}/api/admin/lookup/genitori`);
        if (!resGen.ok) throw new Error('Errore fetch genitori');
        const dataGen = await resGen.json();
        setGenitori(Array.isArray(dataGen) ? dataGen : []);

        try {
          const resAttiva = await authFetch(`${API_URL}/api/admin/stagioni/attiva`);
          if (resAttiva.ok) {
            const attiva = await resAttiva.json();
            if (attiva?.id) {
              setFormData((prev) => ({
                ...prev,
                idStagione: String(attiva.id)
              }));
            }
          }
        } catch {
          // ignoriamo volutamente
        }
      } catch (err) {
        console.error(err);
        setError(err.message || 'Errore nel caricamento dati');
      }
    };

    fetchBase();
  }, [authFetch]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    const rawFields = new Set([
      'idCategoria',
      'idStagione',
      'idGenitore',
      'dataNascita',
      'numeroTesserino',
      'piedePreferito',
      'ruolo',
      'noteSanitarie'
    ]);

    const upperFields = new Set([
      'nome',
      'cognome',
      'luogoNascita'
    ]);

    if (rawFields.has(name)) {
      setFormData((prev) => ({ ...prev, [name]: value }));
      return;
    }

    if (name === 'codiceFiscale') {
      setFormData((prev) => ({
        ...prev,
        [name]: value.replace(/\s+/g, '').toUpperCase()
      }));
      return;
    }

    if (upperFields.has(name)) {
      setFormData((prev) => ({
        ...prev,
        [name]: value.toUpperCase()
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const cf = (formData.codiceFiscale || '').trim().toUpperCase();
    const codiceFiscaleRegex = /^[A-Z0-9]{16}$/;

    if (!codiceFiscaleRegex.test(cf)) {
      setError('❌ Codice fiscale non valido. Deve contenere 16 caratteri (A-Z/0-9).');
      return;
    }

    if (!formData.idCategoria) return setError('❌ Seleziona una categoria');
    if (!formData.idStagione) return setError('❌ Seleziona una stagione');
    if (!formData.idGenitore) return setError('❌ Seleziona un genitore');
    if (!formData.cognome.trim()) return setError('❌ Cognome obbligatorio');
    if (!formData.nome.trim()) return setError('❌ Nome obbligatorio');
    if (!formData.dataNascita) return setError('❌ Data di nascita obbligatoria');
    if (!formData.luogoNascita.trim()) return setError('❌ Luogo di nascita obbligatorio');
    if (!formData.piedePreferito) return setError('❌ Seleziona piede preferito');
    if (!formData.ruolo) return setError('❌ Seleziona ruolo');

    const payload = {
      idCategoria: Number(formData.idCategoria),
      idStagione: Number(formData.idStagione),
      idGenitore: Number(formData.idGenitore),
      codiceFiscale: cf,
      cognome: formData.cognome.trim().toUpperCase(),
      nome: formData.nome.trim().toUpperCase(),
      dataNascita: formData.dataNascita,
      luogoNascita: formData.luogoNascita.trim().toUpperCase(),
      numeroTesserino: formData.numeroTesserino ? formData.numeroTesserino.trim() : null,
      piedePreferito: formData.piedePreferito || null,
      ruolo: formData.ruolo || null,
      noteSanitarie: formData.noteSanitarie?.trim() ? formData.noteSanitarie.trim() : null
    };

    try {
      const response = await authFetch(`${API_URL}/api/admin/giocatori`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      let responseData = null;
      try {
        responseData = await response.json();
      } catch {
        // backend potrebbe non restituire body
      }

      if (!response.ok) {
        const message =
          responseData?.message ||
          responseData?.error ||
          "Errore durante l'inserimento del giocatore";
        throw new Error(message);
      }

      setSuccess('✅ Giocatore inserito con successo!');

      setFormData((prev) => ({
        ...prev,
        idCategoria: '',
        idGenitore: '',
        codiceFiscale: '',
        cognome: '',
        nome: '',
        dataNascita: '',
        luogoNascita: '',
        numeroTesserino: '',
        piedePreferito: '',
        ruolo: '',
        noteSanitarie: ''
      }));
    } catch (err) {
      console.error('Errore submit giocatore:', err);
      setError(err.message || 'Errore submit giocatore');
    }
  };

  const genitoreOptions = [...genitori]
    .sort((a, b) => `${a.cognome} ${a.nome}`.localeCompare(`${b.cognome} ${b.nome}`))
    .map((g) => ({
      value: String(g.id),
      label: `${g.cognome} ${g.nome}${g.email ? ` (${g.email})` : ''}`
    }));

  const selectedGenitore =
    genitoreOptions.find((opt) => opt.value === String(formData.idGenitore)) || null;

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 46,
      borderRadius: 11,
      borderColor: state.isFocused ? '#3506d1' : '#d7d7d7',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(53, 6, 209, 0.16)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#3506d1' : '#cfcfcf'
      }
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 12px'
    }),
    placeholder: (base) => ({
      ...base,
      color: '#777'
    }),
    menu: (base) => ({
      ...base,
      borderRadius: 12,
      overflow: 'hidden',
      zIndex: 20
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? 'rgba(53, 6, 209, 0.08)' : '#ffffff',
      color: '#222',
      cursor: 'pointer'
    })
  };

  return (
    <div className="giocatore-form-container" translate="no">
      <h2 className="giocatore-form-title">Inserisci Nuovo Giocatore</h2>

      {(error || success) && (
        <div
          className={`giocatore-message-card ${
            success ? 'giocatore-success-message' : 'giocatore-error-message'
          }`}
        >
          {success || error}
        </div>
      )}

      <form className="giocatore-form-card" onSubmit={handleSubmit}>
        <div className="giocatore-sezione">
          <h3 className="giocatore-sezione-titolo">Contesto Inserimento</h3>

          <div className="giocatore-form-grid-3">
            <div className="giocatore-form-group">
              <label className="giocatore-label">Categoria</label>
              <select
                className="giocatore-select"
                name="idCategoria"
                value={formData.idCategoria}
                onChange={handleChange}
                required
              >
                <option value="">-- Seleziona una categoria --</option>
                {categorie.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="giocatore-form-group">
              <label className="giocatore-label">Stagione Sportiva</label>
              <select
                className="giocatore-select"
                name="idStagione"
                value={formData.idStagione}
                onChange={handleChange}
                required
              >
                <option value="">-- Seleziona la stagione --</option>
                {stagioni.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                    {s.attiva ? ' (ATTIVA)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="giocatore-form-group">
              <label className="giocatore-label">Genitore</label>
              <Select
                classNamePrefix="giocatore-react-select"
                options={genitoreOptions}
                value={selectedGenitore}
                onChange={(selectedOption) =>
                  setFormData((prev) => ({
                    ...prev,
                    idGenitore: selectedOption ? selectedOption.value : ''
                  }))
                }
                placeholder="Cerca e seleziona un genitore..."
                isClearable
                isSearchable
                noOptionsMessage={() => 'Nessun genitore trovato'}
                styles={selectStyles}
              />
            </div>
          </div>
        </div>

        <div className="giocatore-sezione">
          <h3 className="giocatore-sezione-titolo">Dati Giocatore</h3>

          <div className="giocatore-form-grid">
            <div className="giocatore-form-group">
              <label className="giocatore-label">Cognome</label>
              <input
                className="giocatore-input uppercase"
                type="text"
                name="cognome"
                value={formData.cognome}
                onChange={handleChange}
                required
              />
            </div>

            <div className="giocatore-form-group">
              <label className="giocatore-label">Nome</label>
              <input
                className="giocatore-input uppercase"
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                required
              />
            </div>

            <div className="giocatore-form-group">
              <label className="giocatore-label">Data di Nascita</label>
              <input
                className="giocatore-input"
                type="date"
                name="dataNascita"
                value={formData.dataNascita}
                onChange={handleChange}
                required
              />
            </div>

            <div className="giocatore-form-group">
              <label className="giocatore-label">Luogo di Nascita</label>
              <input
                className="giocatore-input uppercase"
                type="text"
                name="luogoNascita"
                value={formData.luogoNascita}
                onChange={handleChange}
                required
              />
            </div>

            <div className="giocatore-form-group">
              <label className="giocatore-label">Codice Fiscale</label>
              <input
                className="giocatore-input uppercase"
                type="text"
                name="codiceFiscale"
                value={formData.codiceFiscale}
                onChange={handleChange}
                maxLength={16}
                required
              />
            </div>

            <div className="giocatore-form-group">
              <label className="giocatore-label">Numero Tesserino</label>
              <input
                className="giocatore-input"
                type="text"
                name="numeroTesserino"
                value={formData.numeroTesserino}
                onChange={handleChange}
              />
            </div>

            <div className="giocatore-form-group">
              <label className="giocatore-label">Piede Preferito</label>
              <select
                className="giocatore-select"
                name="piedePreferito"
                value={formData.piedePreferito}
                onChange={handleChange}
                required
              >
                <option value="">-- Seleziona piede preferito --</option>
                {PIEDI.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>

            <div className="giocatore-form-group">
              <label className="giocatore-label">Ruolo</label>
              <select
                className="giocatore-select"
                name="ruolo"
                value={formData.ruolo}
                onChange={handleChange}
                required
              >
                <option value="">-- Seleziona ruolo --</option>
                {RUOLI.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            <div className="giocatore-form-group giocatore-full-width">
              <label className="giocatore-label">Note sanitarie</label>
              <textarea
                className="giocatore-textarea"
                name="noteSanitarie"
                value={formData.noteSanitarie}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <div className="giocatore-button-row">
          <button className="giocatore-custom-button giocatore-primary-button" type="submit">
            Salva Giocatore
          </button>
          <button
            className="giocatore-custom-button giocatore-secondary-button"
            type="button"
            onClick={() => navigate('/gestionale')}
          >
            Torna al Gestionale
          </button>
        </div>
      </form>
    </div>
  );
}